import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../stores/authStore";
import { fetchAvailableStores } from "../services/api";
import { Mail, ArrowLeft, Lock, Store, ChevronDown, AlertTriangle } from "lucide-react";
import toast from "react-hot-toast";

type EmergencyStep = "credentials" | "store-selection";

export default function EmergencyLoginPage() {
  const navigate = useNavigate();
  const { 
    emergencyLogin,
    selectStore,
    isLoading, 
    requiresStoreSelection 
  } = useAuthStore();

  const [step, setStep] = useState<EmergencyStep>("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [storeName, setStoreName] = useState("");
  const [availableStores, setAvailableStores] = useState<{ storename: string; api_base_url: string; preview_base_url: string }[]>([]);
  const [loadingStores, setLoadingStores] = useState(false);

  // Carregar stores disponíveis quando necessário
  useEffect(() => {
    const loadStores = async () => {
      if (requiresStoreSelection && availableStores.length === 0) {
        setLoadingStores(true);
        try {
          const stores = await fetchAvailableStores();
          setAvailableStores(stores);
          if (stores.length === 0) {
            toast.error('Nenhuma loja encontrada no sistema');
          }
        } catch (error) {
          console.error('Erro ao carregar stores:', error);
          toast.error('Erro ao carregar lista de lojas');
        } finally {
          setLoadingStores(false);
        }
      }
    };

    loadStores();
  }, [requiresStoreSelection, availableStores.length]);

  const handleEmergencyLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error("Por favor, digite seu email e senha");
      return;
    }

    try {
      await emergencyLogin(email, password, storeName);
      
      // Obter o estado atualizado diretamente do store após emergencyLogin
      const { requiresStoreSelection: currentRequiresStoreSelection } = useAuthStore.getState();
      
      // Se requiresStoreSelection ainda for true após emergencyLogin, 
      // significa que o usuário é multistore e precisa selecionar uma store
      if (currentRequiresStoreSelection) {
        // Ir para o passo de seleção de store
        setStep("store-selection");
        return;
      }
      toast.success("Login de emergência bem-sucedido!");
      navigate("/");
    } catch (error: any) {
      console.error("Erro no login de emergência:", error);
      if (error.message?.includes("Email ou senha inválidos")) {
        toast.error("Email ou senha inválidos. Verifique suas credenciais.");
      } else {
        toast.error("Erro no login de emergência. Tente novamente.");
      }
    }
  };

  const handleSelectStore = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!storeName) {
      toast.error("Por favor, selecione uma loja para acessar.");
      return;
    }

    try {
      await selectStore(storeName);
      toast.success("Login bem-sucedido!");
      navigate("/");
    } catch (error) {
      console.error("Erro ao selecionar loja:", error);
      toast.error("Erro ao selecionar loja. Tente novamente.");
    }
  };

  const handleBackToCredentials = () => {
    setStep("credentials");
    setStoreName("");
  };

  // Passo 1: Credenciais (email e senha)
  if (step === "credentials") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-md shadow-lg p-8">
            {/* Logo */}
            <div className="flex flex-col items-center mb-8">
              <img
                src="/logo.png"
                alt="Site Editor Wicomm"
                className="h-8 w-auto"
              />
              <h1 className="text-xs text-gray-900">Site Editor</h1>
            </div>

            {/* Título e descrição */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <AlertTriangle className="h-8 w-8 text-orange-500 mr-2" />
                <h2 className="text-xl font-semibold text-gray-900">
                  Acesso de Emergência
                </h2>
              </div>
              <p className="text-gray-600">
                Use suas credenciais para acessar o sistema quando o email não estiver funcionando
              </p>
            </div>

            {/* Aviso de segurança */}
            <div className="bg-orange-50 border border-orange-200 rounded-md p-4 mb-6">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-orange-800">
                    Acesso Restrito
                  </p>
                  <p className="text-sm text-orange-700 mt-1">
                    Esta é uma funcionalidade de emergência. Use apenas quando necessário.
                  </p>
                </div>
              </div>
            </div>

            {/* Formulário */}
            <form onSubmit={handleEmergencyLogin} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="seu@email.com.br"
                  />
                </div>
                <p className="mt-2 text-sm text-gray-600">
                  Email cadastrado no sistema
                </p>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Senha
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Sua senha"
                  />
                </div>
                <p className="mt-2 text-sm text-gray-600">
                  Senha cadastrada no Supabase
                </p>
              </div>

              <button
                type="submit"
                disabled={isLoading || !email || !password}
                className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                {isLoading ? "Fazendo login de emergência..." : "Acessar com senha"}
              </button>
            </form>

            {/* Link para voltar ao login normal */}
            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                ← Voltar ao login normal
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Passo 2: Seleção de loja (para usuários multistore)
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-md shadow-lg p-8">
          {/* Header com botão voltar */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={handleBackToCredentials}
              className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 mr-1" />
            </button>
            <div className="flex flex-col items-center">
              <img
                src="/logo.png"
                alt="Site Editor Wicomm"
                className="h-8 w-auto"
              />
              <h1 className="text-xs text-gray-900">Site Editor</h1>
            </div>
            <div className="w-6"></div> {/* Spacer */}
          </div>

          {/* Título e descrição */}
          <div className="text-center mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Selecione a Loja
            </h2>
            <p className="text-gray-600 mb-1">Seu acesso é de desenvolvedor</p>
            <p className="font-medium text-gray-900">{email}</p>
          </div>

          {/* Formulário de seleção de loja */}
          <form onSubmit={handleSelectStore} className="space-y-6">
            <div>
              <label htmlFor="storeName" className="block text-sm font-medium text-gray-700 mb-1">
                Selecione a Loja
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Store className="h-5 w-5 text-gray-400" />
                </div>
                <select
                  id="storeName"
                  required
                  value={storeName}
                  onChange={(e) => setStoreName(e.target.value)}
                  disabled={loadingStores}
                  className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent appearance-none bg-white"
                >
                  <option value="">
                    {loadingStores ? "Carregando lojas..." : "Selecione uma loja"}
                  </option>
                  {availableStores.map((store) => (
                    <option key={store.storename} value={store.storename}>
                      {store.storename}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                </div>
              </div>
              <p className="mt-2 text-sm text-gray-600">
                Selecione a loja que deseja editar.
              </p>
              {loadingStores && (
                <div className="mt-2 flex items-center text-sm text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500 mr-2"></div>
                  Carregando lojas disponíveis...
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading || !storeName}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Store className="h-4 w-4 mr-2" />
              {isLoading ? "Acessando..." : "Acessar loja selecionada"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

